import * as React from "react";
import { useState } from "react";
import { Box, Button, Chip } from "@mui/material";
import { PencilSimpleIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { useParams } from "react-router-dom";

import { campaignsKeys, CustomField, useDeleteCustomField, useDisableCustomField } from "@/hooks/api/use-campaigns";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { useDialog } from "@/hooks/use-dialog";
import { CustomTable } from "@/components/customTable";
import { TableActions } from "@/components/customTable/table-actions";
import { Column } from "@/components/customTable/types";
import { DeleteModal } from "@/components/deleteModal/delete-modal";

import { CreateCustomField } from "./createCustomField";

export function CustomFields(): React.JSX.Element {
	const { campaignId } = useParams();

	const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
	const [selectedCustomField, setSelectedCustomField] = useState<CustomField | null>(null);

	const {
		items: customFields,
		isLoading,
		totalItems,
		refetchData: fetchCustomFields,
	} = useCachedTableStateWithUrl<CustomField>({
		queryKey: campaignsKeys.customFields(campaignId!),
		endpoint: `/campaigns/${campaignId}/fields`,
	});

	const {
		data: selectedCustomFieldToDelete,
		open: isDeleteCustomFieldModalOpen,
		handleClose: handleDeleteCustomFieldModalClose,
		handleOpen: handleDeleteCustomFieldModalOpen,
	} = useDialog<CustomField | null>();

	const deleteCustomFieldMutation = useDeleteCustomField();
	const disableCustomFieldMutation = useDisableCustomField();

	const handleDeleteCustomField = async () => {
		if (selectedCustomFieldToDelete?.id) {
			await deleteCustomFieldMutation.mutateAsync({
				campaignId: campaignId!,
				id: selectedCustomFieldToDelete.id,
			});
			handleDeleteCustomFieldModalClose();
		}
	};

	const handleDisableCustomField = async () => {
		if (selectedCustomFieldToDelete?.id) {
			await disableCustomFieldMutation.mutateAsync({
				campaignId: campaignId!,
				id: selectedCustomFieldToDelete.id,
			});
			handleDeleteCustomFieldModalClose();
		}
	};
	const renderHeaderButtons = () => {
		return (
			<Button startIcon={<PlusIcon />} variant="contained" onClick={() => setIsCreateDrawerOpen(true)}>
				Create Custom Field
			</Button>
		);
	};

	const columns: Column[] = React.useMemo(() => {
		return [
			{
				header: {
					id: "id",
					label: "ID",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "id",
			},
			{
				header: {
					id: "name",
					label: "Name",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "name",
			},

			{
				header: {
					id: "label",
					label: "Label",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "label",
			},
			{
				header: {
					id: "description",
					label: "Description",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "description",
			},
			{
				header: {
					id: "enabled",
					label: "Enabled",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "enabled",
				getCellValue: (row: any) => {
					return (
						<Chip
							color={row.enabled ? "success" : "error"}
							variant="filled"
							size="small"
							label={row.enabled ? "Yes" : "No"}
						/>
					);
				},
			},

			{
				header: {
					id: "actions",
					label: "",
					width: 100,
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "actions",
				getCellValue: (row: any) => (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
						<TableActions
							actions={[
								{
									label: "Edit",
									icon: <PencilSimpleIcon width={20} height={20} weight="fill" />,
									onClick: () => {
										setSelectedCustomField(row);
										setIsCreateDrawerOpen(true);
									},
								},
								{
									label: "Delete",
									icon: <TrashIcon width={20} height={20} weight="fill" />,
									sx: { color: "error.main" },
									onClick: () => handleDeleteCustomFieldModalOpen(row),
								},
							]}
						/>
					</Box>
				),
			},
		];
	}, [handleDeleteCustomFieldModalOpen]);

	return (
		<>
			<CustomTable
				columns={columns}
				data={customFields}
				refetchData={fetchCustomFields}
				isLoading={isLoading}
				totalItems={totalItems}
				tableHeaderTitle="Custom Fields"
				renderHeaderButtons={renderHeaderButtons}
			/>
			<DeleteModal
				open={isDeleteCustomFieldModalOpen}
				onClose={handleDeleteCustomFieldModalClose}
				onDelete={handleDeleteCustomField}
				onAdditionalAction={selectedCustomFieldToDelete?.enabled ? handleDisableCustomField : undefined}
				title="Disable Custom Field"
				description="Are you sure you want to disable this custom field?"
				warningText={
					selectedCustomFieldToDelete?.enabled
						? "Data will be lost. If you want to keep the data but exclude the field from analysis, you can simply disable it."
						: undefined
				}
				additionalActionText={"Disable"}
			/>
			<CreateCustomField
				campaignId={campaignId!}
				customField={selectedCustomField}
				isCreateDrawerOpen={isCreateDrawerOpen}
				onCreateDrawerClose={() => {
					setIsCreateDrawerOpen(false);

					setTimeout(() => {
						setSelectedCustomField(null);
					}, 300);
				}}
			/>
		</>
	);
}
